import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import type { IInputStepRateCost } from 'interfaces/IInputStep';
import { InputStepType } from 'interfaces/IInputStep';
import type { InputTaskCategory } from 'interfaces/IInputTask';
import {
  convertToKg,
  convertToLitres,
  resolveUnitKind,
} from 'utils/unitConversion';
import type { SummaryEntityCategory } from '../../../../redux/slices/InsightsSlice';
import {
  QUANTITY_CATEGORY_ORDER,
  STEP_TYPE_TO_QUANTITY_CATEGORY,
} from 'pages/FarmsPage/Workspace/Insights/costCategories';
import { resolveAreaHa, resolveEffectiveRate } from './insightUtils';

/**
 * Step types that contribute product quantity.
 * Application (Operation) steps are intentionally excluded —
 * they carry a service cost, not a physical product quantity.
 */
const QUANTITY_STEP_TYPES = new Set([
  InputStepType.Seed,
  InputStepType.SoilInput,
  InputStepType.CropProtection,
  InputStepType.Adjuvant,
  InputStepType.SeedDress,
  InputStepType.Biostimulant,
]);

const isValidGlobalId = (id: string | null | undefined): id is string =>
  typeof id === 'string' && id.length > 0;

export interface QuantityRequiredLine {
  /** Grouping key (globalId when available, otherwise item name). */
  key: string;
  /** Product display name. */
  label: string;
  /** Total quantity in the normalised unit (kg for mass, L for liquid). */
  quantity: number;
  /** Normalised unit: 'kg' for mass products, 'L' for liquid products. */
  unit: 'kg' | 'L';
  /** Input category — used for colour coding in the circular bar chart. */
  category: SummaryEntityCategory;
}

export interface QuantityRequiredResult {
  /** Rows grouped by category (QUANTITY_CATEGORY_ORDER order), alphabetical by label within each group. */
  lines: QuantityRequiredLine[];
  hasData: boolean;
}

/**
 * Aggregate per-product quantity across all scoped inputs.
 *
 * ── Aggregation ───────────────────────────────────────────────────────────
 * Iterates tasks whose category is in `categories` — pass both Plan and
 * Actual to match the previous unfiltered behaviour (the Summary lens's own
 * Quantity metric), or a single category to scope the Live Panel's
 * Plan/Actual columns independently.
 * Quantity per step = resolveEffectiveRate × areaHa, in the step's appUnit.
 *
 * ── Unit normalisation ────────────────────────────────────────────────────
 * Mass units → kg, liquid units → L (using unit-conversion helpers).
 * Products with conflicting unit kinds across steps are skipped.
 * Display scaling (kg → t) is left to the panel via scaleForDisplay.
 *
 * ── Grouping ──────────────────────────────────────────────────────────────
 * Products with the same globalId are merged into one row regardless of
 * display name. Falls back to item name when globalId is absent.
 * Output rows are grouped by category (QUANTITY_CATEGORY_ORDER order),
 * alphabetical by label within each group.
 *
 * Pure function — no side effects, no hooks, no Redux.
 */
export const computeQuantityRequired = (
  inputs: IInput[],
  gUnits: GUnit[],
  categories: Set<InputTaskCategory>
): QuantityRequiredResult => {
  const acc = new Map<
    string,
    { label: string; quantity: number; unit: 'kg' | 'L'; category: SummaryEntityCategory }
  >();

  for (const input of inputs) {
    for (const task of input.tasks) {
      if (!categories.has(task.category)) continue;
      for (const step of task.steps) {
        if (!QUANTITY_STEP_TYPES.has(step.stepType)) continue;

        const s = step as unknown as IInputStepRateCost;
        if (!s.appUnit || !step.item) continue;

        const category = STEP_TYPE_TO_QUANTITY_CATEGORY[step.stepType];
        if (!category) continue;

        const key = isValidGlobalId(step.globalId)
          ? step.globalId
          : step.item;

        const effectiveRate = resolveEffectiveRate(step, task, gUnits);
        if (effectiveRate === null || effectiveRate <= 0) continue;

        const areaHa = resolveAreaHa(step, task, input, gUnits);
        if (areaHa <= 0) continue;

        const volume = effectiveRate * areaHa;
        const kind = resolveUnitKind(s.appUnit, gUnits);
        if (!kind) continue;

        const converted =
          kind === 'mass'
            ? convertToKg(volume, s.appUnit, gUnits)
            : convertToLitres(volume, s.appUnit, gUnits);
        if (converted === null || converted <= 0) continue;

        const unit = kind === 'mass' ? ('kg' as const) : ('L' as const);
        const existing = acc.get(key);
        if (existing) {
          // Skip if this step's unit kind conflicts with the row's established kind
          if (existing.unit !== unit) continue;
          acc.set(key, { ...existing, quantity: existing.quantity + converted });
        } else {
          acc.set(key, { label: step.item, quantity: converted, unit, category });
        }
      }
    }
  }

  const categoryRank = new Map<SummaryEntityCategory, number>(
    QUANTITY_CATEGORY_ORDER.map((cat, i) => [cat, i])
  );

  const lines: QuantityRequiredLine[] = Array.from(acc.entries())
    .filter(([, v]) => v.quantity > 0)
    .map(([key, v]) => ({ key, ...v }))
    .sort((a, b) => {
      const rankDiff =
        (categoryRank.get(a.category) ?? 0) - (categoryRank.get(b.category) ?? 0);
      if (rankDiff !== 0) return rankDiff;
      return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
    });

  return { lines, hasData: lines.length > 0 };
};
